'use client';

export {};

declare global {
  interface Window {
    tgp: (action: string, id: string) => void;
  }
}

import { useMutation } from '@apollo/client/react';
import { PortalModal, usePortalContext } from '@cloai/uikit';

import { useTranslations } from 'next-intl';
import { FC, useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { SEND_KEYBOARD } from '@/.graphql';
import AiVideoIcon from '@/public/icons/tools/ai_video.svg';
import AnimatePhotoIcon from '@/public/icons/tools/animate_photo.svg';
import CongratulationsIcon from '@/public/icons/tools/celebration.svg';
import FlatlayIcon from '@/public/icons/tools/flat_lay.svg';
import FuturebabyIcon from '@/public/icons/tools/future_baby.svg';
import GodModeIcon from '@/public/icons/tools/godmode.svg';
import InpaintIcon from '@/public/icons/tools/inpaint.svg';
import PhotoByPhotoIcon from '@/public/icons/tools/photo_by_photo.svg';
import PhotosessionsIcon from '@/public/icons/tools/photosessions.svg';
import PromodeIcon from '@/public/icons/tools/pro_mode.svg';
import RestorationIcon from '@/public/icons/tools/restoration.svg';
import SongIcon from '@/public/icons/tools/song.svg';
import SpeakingPhotoIcon from '@/public/icons/tools/speaking_photo.svg';
import StickersIcon from '@/public/icons/tools/stickers.svg';
import StylesIcon from '@/public/icons/tools/styles.svg';
import Veo3Icon from '@/public/icons/tools/veo3.svg';
import NoAvatarIcon from '@/public/icons/tools/video_photo_no_avatar.svg';
import { Footer } from '@/src/components/Footer';
import { Header } from '@/src/components/Header';
import {
  useStyleContext,
  useGTMContext,
  useProfileContext,
  useMobileContext,
} from '@/src/providers';

import { ToolCard } from './ToolCard';
import { miniApp } from '@tma.js/sdk-react';

// Constants
const STYLES = {
  portal: '!h-screen !max-h-screen !rounded-none !bg-none !bg-fill-menu !p-0',
  wrapper: 'mt-2 flex flex-col gap-2 py-16 mb-16 px-5 sm:max-w-[458px] m-auto',
  header:
    'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center bg-fill-menu dark:bg-fill-menu bg-fill-menuBg',
  content: 'h-full overflow-y-auto',
  section: 'flex flex-col gap-2 w-full',
  grid: 'flex gap-2 justify-between w-full',
  title: 'font-bold text-lg',
  icon: 'w-[30px] h-5 flex align-center justify-center',
  mobileOffset: 'pt-[100px]',
} as const;

const KEYBOARD_ACTIONS = {
  song: 'audioMenu',
  talkingPhoto: 'photoVoiceoverMenu',
  restoration: 'restorationMenu',
  congratulations: 'sendCongratulationMenu',
  proMode: 'proModeMenu',
  videoNoAvatar: 'noAvatarVideo',
  photoNoAvatar: 'noAvatarMode',
  flatLay: 'startFlatLayConversation',
  futureBaby: 'futureBabyMenu',
} as const;

interface BaseToolConfig {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  imageKey: string;
  delay: number;
  size?: 'normal' | 'small';
}

interface PortalToolConfig extends BaseToolConfig {
  type: 'portal';
  portalId: string;
}

interface KeyboardToolConfig extends BaseToolConfig {
  type: 'keyboard';
  keyboardAction: keyof typeof KEYBOARD_ACTIONS;
}

interface HrefToolConfig extends BaseToolConfig {
  type: 'href';
  href: string;
}

interface SimpleToolConfig extends BaseToolConfig {
  type: 'simple';
}

type ToolConfig =
  | PortalToolConfig
  | KeyboardToolConfig
  | HrefToolConfig
  | SimpleToolConfig;

interface ToolSection {
  titleKey: string;
  tools: ToolConfig[];
  layout: 'grid' | 'custom';
}

export const AllToolsPortal: FC = () => {
  // const webApp = useTelegramWebApp();
  const profile = useProfileContext()!;
  const sendGTM = useGTMContext();
  const t = useTranslations('allToolsModal');
  const { closePortal, openPortal } = usePortalContext();
  const { setPrevPortal } = useStyleContext();
  const { isMobiles } = useMobileContext();

  const [sendKeyboard, { loading }] = useMutation(SEND_KEYBOARD);

  // Unified action handlers
  const handlePortalAction = useCallback(
    (portalId: string) => {
      setPrevPortal('allTools');
      closePortal();
      openPortal(portalId);
    },
    [setPrevPortal, closePortal, openPortal],
  );

  const handleKeyboardAction = useCallback(
    async (keyboardType: string) => {
      try {
        await sendKeyboard({ variables: { keyboard: keyboardType } });
        if (miniApp.close.isAvailable()) {
          miniApp.close();
        }
      } catch (error) {
        console.error('Keyboard action failed:', error);
      }
    },
    [sendKeyboard],
  );

  const handleSimpleAction = useCallback(() => {
    closePortal();
  }, [closePortal]);

  // Unified tool click handler
  const createToolHandler = useCallback(
    (tool: ToolConfig) => {
      switch (tool.type) {
        case 'portal':
          return () => handlePortalAction(tool.portalId);
        case 'keyboard':
          return () =>
            handleKeyboardAction(KEYBOARD_ACTIONS[tool.keyboardAction]);
        case 'href':
        case 'simple':
          return handleSimpleAction;
        default:
          return handleSimpleAction;
      }
    },
    [handlePortalAction, handleKeyboardAction, handleSimpleAction],
  );

  // Tool configurations
  const toolSections: ToolSection[] = useMemo(
    () => [
      {
        titleKey: 'creationTitle',
        layout: 'custom',
        tools: [
          {
            id: 'animatePhoto',
            type: 'portal',
            portalId: PORTALS.AnyImageToVideo,
            icon: AnimatePhotoIcon,
            imageKey: 'tool-animate',
            delay: 200,
            size: 'normal',
          },
          {
            id: 'styles',
            type: 'href',
            href: '/#styles',
            icon: StylesIcon,
            imageKey: 'tool-styles',
            delay: 800,
            size: 'small',
          },
          {
            id: 'godMode',
            type: 'portal',
            portalId: PORTALS.GodMode,
            icon: GodModeIcon,
            imageKey: 'tool-god',
            delay: 1000,
            size: 'small',
          },
          {
            id: 'aiVideo',
            type: 'href',
            href: '/#video',
            icon: AiVideoIcon,
            imageKey: 'tool-video',
            delay: 800,
            size: 'small',
          },
          {
            id: 'photossesions',
            type: 'href',
            href: '/#photoshoots',
            icon: PhotosessionsIcon,
            imageKey: 'tool-photosession',
            delay: 1000,
            size: 'small',
          },
          {
            id: 'song',
            type: 'keyboard',
            keyboardAction: 'song',
            icon: SongIcon,
            imageKey: 'tool-song',
            delay: 200,
            size: 'small',
          },
          {
            id: 'stickers',
            type: 'href',
            href: '/#stickers',
            icon: StickersIcon,
            imageKey: 'tool-stickers',
            delay: 1000,
            size: 'small',
          },
          {
            id: 'flatLay',
            type: 'keyboard',
            keyboardAction: 'flatLay',
            icon: FlatlayIcon,
            imageKey: 'tool-flatlay',
            delay: 1000,
            size: 'small',
          },
          {
            id: 'futureBaby',
            type: 'keyboard',
            keyboardAction: 'futureBaby',
            icon: FuturebabyIcon,
            imageKey: 'tool-futurebaby',
            delay: 1500,
            size: 'small',
          },
          {
            id: 'proMode',
            type: 'keyboard',
            keyboardAction: 'proMode',
            icon: PromodeIcon,
            imageKey: 'tool-promode',
            delay: 200,
            size: 'normal',
          },
          {
            id: 'veo3',
            type: 'href',
            href: '/#veo3',
            icon: Veo3Icon,
            imageKey: 'tool-veo3',
            delay: 1000,
            size: 'small',
          },
          {
            id: 'photoNoAvatar',
            type: 'keyboard',
            keyboardAction: 'photoNoAvatar',
            icon: NoAvatarIcon,
            imageKey: 'tool-photo-no-avatar',
            delay: 200,
            size: 'small',
          },
        ],
      },
      {
        titleKey: 'correctionTitle',
        layout: 'grid',
        tools: [
          {
            id: 'inpaint',
            type: 'portal',
            portalId: PORTALS.GenerateInpaint,
            icon: InpaintIcon,
            imageKey: 'tool-inpaint',
            delay: 200,
            size: 'small',
          },
          {
            id: 'restoration',
            type: 'keyboard',
            keyboardAction: 'restoration',
            icon: RestorationIcon,
            imageKey: 'tool-restoration',
            delay: 1500,
            size: 'small',
          },
        ],
      },
      {
        titleKey: 'repeatTitle',
        layout: 'grid',
        tools: [
          {
            id: 'photoByPhoto',
            type: 'portal',
            portalId: PORTALS.GeneratePhotoByPhoto,
            icon: PhotoByPhotoIcon,
            imageKey: 'tool-photo-by-photo',
            delay: 800,
            size: 'small',
          },
        ],
      },
    ],
    [],
  );

  // Render functions
  const renderToolCard = useCallback(
    (tool: ToolConfig) => {
      const Icon = tool.icon;
      return (
        <ToolCard
          key={tool.id}
          icon={<Icon className={STYLES.icon} />}
          name={t(tool.id)}
          image={`/images/tools/${tool.imageKey}.webp`}
          delay={tool.delay}
          pause={tool.delay}
          isSmall={tool.size === 'small'}
          href={tool.type === 'href' ? tool.href : undefined}
          handleClickImage={createToolHandler(tool)}
          disabled={loading}
        />
      );
    },
    [t, createToolHandler, loading],
  );

  const renderCreationSection = useCallback(
    (tools: ToolConfig[]) => (
      <div className={STYLES.section}>
        <div className={STYLES.title}>{t('creationTitle')}</div>
        <div className={STYLES.grid}>
          {renderToolCard(tools[0])}
          <div className={STYLES.section}>
            {renderToolCard(tools[1])}
            {renderToolCard(tools[2])}
          </div>
        </div>
        <div className={STYLES.grid}>
          <div className={STYLES.section}>
            {renderToolCard(tools[3])}
            {renderToolCard(tools[4])}
          </div>
          <div className={STYLES.section}>
            <div className={STYLES.grid}>
              {renderToolCard(tools[5])}
              {renderToolCard(tools[6])}
            </div>
            <div className={STYLES.grid}>
              {renderToolCard(tools[7])}
              {renderToolCard(tools[8])}
            </div>
          </div>
        </div>
        <div className={STYLES.grid}>
          {renderToolCard(tools[9])}
          <div className={STYLES.section}>
            {renderToolCard(tools[10])}
            {renderToolCard(tools[11])}
          </div>
        </div>
      </div>
    ),
    [t, renderToolCard],
  );

  const renderGridSection = useCallback(
    (section: ToolSection) => (
      <div className={STYLES.section}>
        <div className={STYLES.title}>{t(section.titleKey)}</div>
        <div className={STYLES.grid}>{section.tools.map(renderToolCard)}</div>
      </div>
    ),
    [t, renderToolCard],
  );

  const renderSection = useCallback(
    (section: ToolSection) => {
      if (section.layout === 'custom' && section.titleKey === 'creationTitle') {
        return renderCreationSection(section.tools);
      }
      return renderGridSection(section);
    },
    [renderCreationSection, renderGridSection],
  );

  return (
    <PortalModal portalId={PORTALS.AllTools} className={STYLES.portal} noClose>
      <div className="h-full relative">
        <div
          className={twMerge(STYLES.header, isMobiles && STYLES.mobileOffset)}
        >
          <Header isCloseButton onClickCloseButton={closePortal} />
        </div>

        <div
          className={twMerge(STYLES.content, isMobiles && STYLES.mobileOffset)}
        >
          <div className={STYLES.wrapper}>
            {toolSections.map((section, index) => (
              <div key={section.titleKey}>{renderSection(section)}</div>
            ))}
            <Footer />
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
